import type { Feature, Polygon, MultiPolygon, FeatureCollection } from "geojson";
import bbox from "@turf/bbox";
import centroid from "@turf/centroid";

export const getRegionBounds = (region: FeatureCollection): [number, number, number, number] => {
    return bbox(region) as [number, number, number, number];
};

export const getDistrictLabels = (districts: FeatureCollection): FeatureCollection => {
    return {
        type: "FeatureCollection",
        features: districts.features.map((f) => {
            const c = centroid(f);
            c.properties = { district: f.properties?.district || "" };
            return c;
        }),
    };
};

export const getOmskSolidFeature = (
    districts: FeatureCollection,
    districtName: string
): Feature<Polygon | MultiPolygon> | undefined => {
    const raw = districts.features.find(f => f.properties?.district === districtName);
    if(!raw) return undefined;

    const geom = raw.geometry;
    if(geom.type === "Polygon") {
        return {
            type: "Feature",
            properties: raw.properties,
            geometry: { type: "Polygon", coordinates: [geom.coordinates[0]] },
        };
    } else if (geom.type === "MultiPolygon") {
        const outerRings = geom.coordinates.map(poly => poly[0]);
        return {
            type: "Feature",
            properties: raw.properties,
            geometry: { type: "MultiPolygon", coordinates: outerRings.map(r => [r]) },
        };
    }
    return undefined;
};

export const getDistrictsWithout = (
    districts: FeatureCollection,
    excludeName: string
): FeatureCollection => ({
    type: "FeatureCollection",
    features: districts.features.filter(f => f.properties?.district !== excludeName),
});

export function getMaskGeoJSON(regionData: FeatureCollection): Feature<Polygon> {
    const geom = regionData.features[0].geometry;

    const regionRings: number[][][] =
        geom.type === "Polygon"
            ? geom.coordinates
            : geom.type === "MultiPolygon"
                ? geom.coordinates.flat()
                : [];

    return {
        type: "Feature",
        properties: {},
        geometry: {
            type: "Polygon",
            coordinates: [
                [
                    [-180, -90],
                    [180, -90],
                    [180, 90],
                    [-180, 90],
                    [-180, -90],
                ],
                ...regionRings,
            ],
        },
    };
}

export const extendBounds = (
    bounds: [number, number, number, number],
    horizontalFactor = 0.2,
    verticalFactor = 0.2
): [number, number, number, number] => {
    const [west, south, east, north] = bounds;
    const lngSpan = east - west;
    const latSpan = north - south;
    return [
        west - lngSpan * horizontalFactor,
        south - latSpan * verticalFactor,
        east + lngSpan * horizontalFactor,
        north + latSpan * verticalFactor,
    ];
};

export const getFactorsFromAspectRatio = (width: number, height: number) => {
    const aspect = width / height;
    const baseHorizontal = 0.25;
    const baseVertical = 0.1;

    if(aspect > 1) {
        return {
            horizontal: (baseHorizontal + 0.3) * aspect,
            vertical: baseVertical / aspect,
        };
    } else {
        return {
            horizontal: baseHorizontal * aspect,
            vertical: baseVertical / aspect,
        };
    }
};